<!--
  The document dropdown. Kept, and reconciled rather than duplicated.

  TASK-042 §3c: this is no longer what LIST view renders -- list rows carry
  `DocRowActions`, three real buttons, no menu (§3b). It survives because a
  TILE is too small to carry three labelled buttons and too dense to show them
  all at once, so icon view (§5, phase 5) anchors this same dropdown in the
  tile's top-right corner. Its hard-won bits are exactly what a tile needs and
  would otherwise be rewritten from scratch: the outside-click and Escape
  handlers registered only while the menu is open, the `aria-haspopup`/
  `aria-expanded` pair, and the per-document `aria-label`.

  What it no longer does is DECIDE. The `isOwner` / `archived` branching that
  used to live here is gone, replaced by a `docActions()` call, so the two
  presentations of one action set cannot drift into offering different things.
  Nothing there is inferred either: `doc.role` comes from the server on every
  row of `GET /v1/docs`, computed by the same expression `assertDocAccess`
  enforces.

  Share… and Remove from my list are here as of TASK-038 phase 2, and they come
  from `docActions()` like everything else, so the dropdown and the row buttons
  cannot drift into offering different things. The access list is not a menu
  item: it is the bottom half of the share dialog, because "who has this" and
  "give it to somebody" are one question asked twice.

  Still not offered, because they still do not exist: Hand over ownership
  (TASK-038 phase 3) and anything about sharing a FOLDER (TASK-040).
-->
<script lang="ts">
  import type { DocActionKind, DocRow } from './doclist-types'
  import { docActions } from './doclist-types'

  let {
    doc,
    archived = false,
    onshare,
    onrename,
    onmove,
    ondelete,
    onleave,
    onrestore,
  }: {
    doc: DocRow
    archived?: boolean
    onshare: (doc: DocRow) => void
    onrename: (doc: DocRow) => void
    onmove: (doc: DocRow) => void
    ondelete: (doc: DocRow) => void
    onleave: (doc: DocRow) => void
    onrestore: (doc: DocRow) => void
  } = $props()

  let open = $state(false)
  let root = $state<HTMLElement | null>(null)

  const actions = $derived(docActions(doc, { archived }))

  const LABEL: Record<DocActionKind, string> = {
    share: 'Share…',
    rename: 'Rename…',
    move: 'Move to…',
    delete: 'Delete…',
    leave: 'Remove from my list…',
    restore: 'Restore',
  }

  function pick(kind: DocActionKind) {
    if (kind === 'share') run(onshare)
    else if (kind === 'rename') run(onrename)
    else if (kind === 'move') run(onmove)
    else if (kind === 'delete') run(ondelete)
    else if (kind === 'leave') run(onleave)
    else run(onrestore)
  }

  function close() {
    open = false
  }

  function run(action: (doc: DocRow) => void) {
    close()
    action(doc)
  }

  /*
   * A click anywhere else closes the menu, and Escape closes it from the
   * keyboard. Registered only while the menu is open, so a list of a hundred
   * rows does not carry a hundred document listeners.
   */
  $effect(() => {
    if (!open) return
    const onDocumentClick = (event: MouseEvent) => {
      if (root && !root.contains(event.target as Node)) close()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('click', onDocumentClick, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onDocumentClick, true)
      document.removeEventListener('keydown', onKey)
    }
  })
</script>

<div class="menu" bind:this={root}>
  <button
    type="button"
    class="menu-button"
    aria-haspopup="menu"
    aria-expanded={open}
    aria-label={`Actions for ${doc.name}`}
    onclick={() => (open = !open)}
  >
    <span aria-hidden="true">•••</span>
  </button>

  {#if open}
    <!-- One `docActions()` call, rendered. Filing needs only `read`, which is
         why Move to... is in the set for a viewer; that rule lives in
         `docActions` now, next to the other three. -->
    <div class="menu-items" role="menu">
      {#each actions as kind (kind)}
        <button
          type="button"
          role="menuitem"
          class:danger={kind === 'delete' || kind === 'leave'}
          onclick={() => pick(kind)}
        >
          {LABEL[kind]}
        </button>
      {/each}
    </div>
  {/if}
</div>

<style>
  .menu {
    position: relative;
    flex: 0 0 auto;
  }

  .menu-button {
    border: 0;
    background: transparent;
    color: var(--color-ink-soft, #6e665c);
    font: inherit;
    line-height: 1;
    padding: 0.35rem 0.5rem;
    border-radius: 0.375rem;
    cursor: pointer;
  }

  .menu-button:hover,
  .menu-button[aria-expanded='true'] {
    background: var(--color-plinth, #f1ece4);
    color: var(--color-ink, #211d18);
  }

  .menu-items {
    position: absolute;
    right: 0;
    top: calc(100% + 0.25rem);
    z-index: 20;
    min-width: 11rem;
    display: flex;
    flex-direction: column;
    padding: 0.25rem;
    border: 1px solid var(--color-line, #211d1824);
    border-radius: 0.5rem;
    background: var(--color-paper, #faf9f5);
    box-shadow: 0 8px 24px rgb(0 0 0 / 12%);
  }

  .menu-items button {
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    padding: 0.45rem 0.6rem;
    border-radius: 0.375rem;
    cursor: pointer;
  }

  .menu-items button:hover {
    background: var(--color-plinth, #f1ece4);
  }

  .menu-items .danger {
    color: var(--color-red-deep, #c01030);
  }
</style>
